/*
 * DAILE Mono — "View on your wall" with the customer's own photo (round 9, 2026-10-06).
 * Loaded by snippets/mn-ar-cz.liquid (sections/mn-com-cz-skin.liquid) on the "Make it personal" customizer — the
 * page /pages/customizer?view=mono-customizer and the same page inside the customizer pop-up (mn-czpop iframe).
 *
 * Reads the customizer, never changes it (assets/dv-customizer.js is untouched):
 *   window.DVCustomizer.instance → getState(): set, photo, crop { zoom, nx, ny }, orientation; store.subscribe();
 *   window.DVCustomizer._test → cropRect / bleedFor / trimFor (the customizer's own crop maths);
 *   [data-dv-cz-proxy-photo] → the customer's photo as the customizer uses it for previews (≤ 2400 px, JPEG blob);
 *   window.DVCZ.sceneFor(...) → the puzzle slot's aspect (= the set's trim, e.g. 45 : 65).
 * The pill appears on the puzzle preview of the PHOTO step and on the REVIEW preview once there is a photo. A tap
 * crops the print area exactly as positioned (cropRect with the set's bleed = what gets printed, not the box
 * mockup) and opens assets/mn-ar.js with it: mnAr.open({ image, key, custom: true }).
 *
 * WHERE THE OVERLAY OPENS
 * - Customizer page: here.
 * - Customizer pop-up (this document is an iframe of the same origin): in the TOP page. mn-ar.js is added to the
 *   top document (or reused when the PDP pill already loaded it) and gets this page's config as window.__mnArCfg
 *   when the top page has none. The overlay <dialog> is shown modally over the pop-up; "Open camera" is then
 *   tapped in the top document, so AR Quick Look (iOS: <a rel="ar"> clicked inside a user gesture) and WebXR
 *   (Android: immersive-ar session from a user gesture; frames would need allow="xr-spatial-tracking") start from
 *   a top-level gesture. Should the top page be out of reach, it opens inside the frame instead.
 */
(() => {
  'use strict';

  const w = window;
  const d = document;
  if (w.mnArCz) return;
  const cfgEl = d.querySelector('[data-mn-ar-cfg]');
  if (!cfgEl || !(w.WebGL2RenderingContext || w.WebGLRenderingContext) || !w.fetch || !w.Blob || !w.Promise || !w.Path2D) return;
  let cfg;
  try { cfg = JSON.parse(cfgEl.textContent); } catch (e) { return; }
  const t = cfg.t || {};
  w.mnArCz = { version: 1 };

  // Customizer set → AR size key.
  const KEYS = { mini300: 'mini-300', mini500: 'mini-500', classic: 'classic', signature: 'signature' };
  const STAGES = ['upload', 'review'];
  const ICON = '<svg class="mn-i" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M3.75 7.5V5.25a1.5 1.5 0 0 1 1.5-1.5H7.5M16.5 3.75h2.25a1.5 1.5 0 0 1 1.5 1.5V7.5M20.25 16.5v2.25a1.5 1.5 0 0 1-1.5 1.5H16.5M7.5 20.25H5.25a1.5 1.5 0 0 1-1.5-1.5V16.5"/><path d="M12 7.25 16.25 9.6v4.8L12 16.75 7.75 14.4V9.6L12 7.25Z"/><path d="M7.75 9.6 12 11.95l4.25-2.35M12 11.95v4.8"/></svg>';

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
  const api = () => (w.DVCustomizer && w.DVCustomizer.instance) || null;
  const proxy = () => d.querySelector('[data-dv-cz-proxy-photo]');
  // The customizer's crop maths (exported for its tests): without them the pill stays hidden.
  const maths = () => { const T = w.DVCustomizer && w.DVCustomizer._test; return !!(T && T.cropRect && T.bleedFor && T.trimFor); };
  const ready = (st) => !!(st && st.photo && st.set && KEYS[st.set] && maths());

  /* Host window: the top page when this customizer runs in the pop-up's iframe. */
  const host = () => {
    try {
      const top = w.top;
      if (top && top !== w && top.location.origin === w.location.origin && top.document.querySelector('[data-mn-czpop]')) return top;
    } catch (e) { /* not reachable */ }
    return w;
  };

  const loadAr = (win) => {
    if (win.mnAr) return Promise.resolve(win.mnAr);
    if (win.__mnArLoading) return win.__mnArLoading;
    const doc = win.document;
    if (!doc.querySelector('[data-mn-ar-cfg]')) win.__mnArCfg = win.JSON.parse(JSON.stringify(cfg));
    win.__mnArLoading = new Promise((ok, ko) => {
      const s = doc.createElement('script');
      s.src = cfg.js;
      s.async = true;
      s.onload = () => (win.mnAr ? ok(win.mnAr) : ko(new Error('mn-ar did not start')));
      s.onerror = () => ko(new Error('mn-ar.js'));
      doc.head.appendChild(s);
    });
    win.__mnArLoading.catch(() => { win.__mnArLoading = null; });
    return win.__mnArLoading;
  };

  /* The print area of the photo, exactly as positioned (cropRect with the set's bleed = the trimmed puzzle). */
  const cropBlob = async () => {
    const inst = api();
    const T = w.DVCustomizer && w.DVCustomizer._test;
    const st = inst && inst.getState();
    if (!ready(st) || !T || !T.cropRect) throw new Error('no photo');
    const img = proxy();
    for (let i = 0; i < 40 && img && !(img.getAttribute('src') && img.complete && img.naturalWidth); i += 1) await sleep(100);
    if (!img || !img.naturalWidth) throw new Error('photo not ready');
    const o = st.orientation === 'h' ? 'h' : 'v';
    const trim = T.trimFor(st.set, o);
    let aspect = trim[0] / trim[1];
    try {
      const scene = w.DVCZ.sceneFor({ set: st.set, orientation: o === 'h' ? 'horizontal' : 'vertical', kind: 'puzzle' });
      if (scene && scene.slots && scene.slots[0] && scene.slots[0].aspect) aspect = scene.slots[0].aspect;
    } catch (e) { /* trim aspect */ }
    const pw = img.naturalWidth;
    const ph = img.naturalHeight;
    const r = T.cropRect(pw, ph, aspect, st.crop, T.bleedFor(st.set, o));
    const sw = r[2] * pw;
    const sh = r[3] * ph;
    const k = Math.min(1, 2400 / Math.max(sw, sh));
    const c = d.createElement('canvas');
    c.width = Math.max(1, Math.round(sw * k));
    c.height = Math.max(1, Math.round(sh * k));
    const ctx = c.getContext('2d');
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(img, r[0] * pw, r[1] * ph, sw, sh, 0, 0, c.width, c.height);
    const blob = await new Promise((ok, ko) => c.toBlob((b) => (b ? ok(b) : ko(new Error('jpeg'))), 'image/jpeg', 0.92));
    c.width = 0;
    c.height = 0;
    return { blob, key: KEYS[st.set], id: [img.getAttribute('src'), st.set, o, st.crop.zoom, st.crop.nx, st.crop.ny].join('|') };
  };

  /* Pills ==================================================================== */

  const pills = [];
  let opening = false;

  const onTap = async (pill) => {
    if (opening) return;
    opening = true;
    pill.setAttribute('aria-busy', 'true');
    try {
      const win = host();
      const [shot, ar] = await Promise.all([cropBlob(), loadAr(win).catch(() => (win !== w ? loadAr(w) : Promise.reject()))]);
      // The image is handed over as bytes in the host's own Blob (no cross-document object URLs).
      const owner = ar === win.mnAr ? win : w;
      const image = owner === w ? shot.blob : new owner.Blob([await shot.blob.arrayBuffer()], { type: 'image/jpeg' });
      await ar.open({ image, key: shot.key, custom: true, id: shot.id, title: t.czTitle, returnFocus: owner === w ? pill : null, onClose: () => { try { pill.focus({ preventScroll: true }); } catch (e) { /* gone */ } } });
    } catch (e) {
      if (w.console) console.warn('[mn-ar-cz]', e);
    } finally {
      opening = false;
      pill.removeAttribute('aria-busy');
    }
  };

  const makePill = (stage) => {
    const b = d.createElement('button');
    b.type = 'button';
    b.className = 'mn-ar-btn mn-ar-btn--cz';
    b.setAttribute('data-mn-ar-cz', '');
    b.setAttribute('aria-haspopup', 'dialog');
    b.hidden = true;
    b.innerHTML = `${ICON}<span></span>`;
    b.lastChild.textContent = t.btn || 'View on your wall';
    // Warm up on the first touch: mn-ar.js in the host page (model-viewer and the CSS come with open()).
    b.addEventListener('pointerdown', () => { loadAr(host()).catch(() => {}); }, { once: true });
    b.addEventListener('click', () => onTap(b));
    stage.appendChild(b);
    return b;
  };

  const sync = () => {
    const inst = api();
    const show = ready(inst && inst.getState());
    pills.forEach((b) => { b.hidden = !show; });
  };

  let bound = null;
  let unsub = null;
  const bind = () => {
    const inst = api();
    if (!inst || inst === bound) return !!inst;
    if (unsub) { try { unsub(); } catch (e) { /* old instance */ } }
    bound = inst;
    STAGES.forEach((name) => {
      const stage = d.querySelector(`[data-dv-cz-stage="${name}"]`);
      if (stage && !stage.querySelector('[data-mn-ar-cz]')) pills.push(makePill(stage));
    });
    unsub = inst.store && inst.store.subscribe ? inst.store.subscribe(sync) : null;
    sync();
    return true;
  };

  // The customizer mounts from its own deferred script (via assets/dv.js): wait for it (≤ 60 s), then follow it.
  let tries = 0;
  const wait = () => {
    if (bind()) return;
    tries += 1;
    if (tries < 240) setTimeout(wait, 250);
  };
  if (d.readyState === 'loading') d.addEventListener('DOMContentLoaded', wait, { once: true });
  else wait();
  // Theme editor: the section can be re-mounted.
  d.addEventListener('shopify:section:load', () => { tries = 0; setTimeout(wait, 300); });
})();
